/**
 * trackBoundSessionSeed — what a track-bound session already knows.
 *
 * Owner ruling (2026-07-27), the second half of the S1–S4 rule in
 * [sessionStepInheritance]: a session created from a track / cycle / loop must
 * not re-ask what the chain already answered. The ask is the loop's request,
 * the options are the track's item pool, the people are the track's
 * participants.
 *
 * The hard constraint, and the easy thing to get wrong: *derive when there is
 * something to derive, ask when there isn't*. Every field below reports its
 * source, and `'none'` is a first-class answer meaning "the track left this
 * blank — the session configures it, exactly as a standalone session would".
 * Nothing here may ever become a precondition: an empty track never blocks a
 * session (a simplified cycle never even offers some steps at the track — see
 * `visibleFrameDesignTabs`).
 *
 * Pure on purpose: the phone wizard is not the only surface that will need
 * this, and inheritance has to be inspectable (build contract §2.4a).
 */

/** The slice of `LoopIntent` a session seed reads. */
export interface SeedLoopIntent {
  asks?: string[];
  strategicContext?: string;
}

export interface SeedItem {
  id: string;
  title: string;
}

export interface SeedPerson {
  userId: string;
  name: string;
}

export type AskSource = 'loop_intent' | 'track_name' | 'none';
export type ItemsSource = 'track_pool' | 'none';
export type PeopleSource = 'track_participants' | 'none';

export interface TrackBoundSeedInput {
  /** Name of the track this session belongs to, when it belongs to one. */
  trackName?: string | null;
  /** The loop's request — what the receiving level has been asked to do. */
  loopIntent?: SeedLoopIntent | null;
  /** Items on the track's pool, already resolved to titles. */
  poolItems?: readonly SeedItem[] | null;
  /** Active participants on the track, already resolved to display names. */
  participants?: readonly SeedPerson[] | null;
}

export interface TrackBoundSeed {
  /** Session name / decision statement. `''` when nothing could be derived. */
  ask: string;
  askSource: AskSource;
  /** The loop's own words, when it carries a request — shown, never edited here. */
  requestLine: string | null;
  items: SeedItem[];
  itemsSource: ItemsSource;
  people: SeedPerson[];
  peopleSource: PeopleSource;
  /** True when at least one field came from the chain rather than the user. */
  anyDerived: boolean;
}

const MAX_ASK = 200;

function firstNonEmpty(values: readonly (string | undefined)[]): string | null {
  for (const v of values) {
    const trimmed = (v ?? '').trim();
    if (trimmed) return trimmed;
  }
  return null;
}

/**
 * The one sentence this session is deciding.
 *
 * The loop's first ask is the most specific thing anyone said about this
 * request, so it wins; strategic context is the next best; the track name is
 * the last resort ("Prioritize: Cloud Servers"). No source → the wizard asks.
 */
export function deriveSessionAsk(
  loopIntent: SeedLoopIntent | null | undefined,
  trackName: string | null | undefined,
): { ask: string; source: AskSource } {
  const fromIntent = firstNonEmpty([
    ...(loopIntent?.asks ?? []),
    loopIntent?.strategicContext,
  ]);
  if (fromIntent) return { ask: fromIntent.slice(0, MAX_ASK), source: 'loop_intent' };

  const name = (trackName ?? '').trim();
  if (name) return { ask: `Prioritize: ${name}`.slice(0, MAX_ASK), source: 'track_name' };

  return { ask: '', source: 'none' };
}

export function deriveTrackBoundSeed(input: TrackBoundSeedInput): TrackBoundSeed {
  const { ask, source: askSource } = deriveSessionAsk(input.loopIntent, input.trackName);

  const items = (input.poolItems ?? []).filter((i) => i && i.id && i.title);
  const people = (input.participants ?? []).filter((p) => p && p.userId);

  const requestLine = firstNonEmpty([
    ...(input.loopIntent?.asks ?? []),
    input.loopIntent?.strategicContext,
  ]);

  return {
    ask,
    askSource,
    requestLine,
    items: [...items],
    itemsSource: items.length > 0 ? 'track_pool' : 'none',
    people: [...people],
    peopleSource: people.length > 0 ? 'track_participants' : 'none',
    anyDerived:
      askSource !== 'none' || items.length > 0 || people.length > 0,
  };
}

/** A session with no track: nothing to derive, the wizard asks everything. */
export const STANDALONE_SEED: TrackBoundSeed = deriveTrackBoundSeed({});

/**
 * The platform items a sitting will actually rank: the track pool plus whatever
 * the entry point pre-selected, deduped by id, minus anything the facilitator
 * dropped. A locked pool (S2 `locked`) ignores drops — it is read-only.
 */
export function mergeSessionItems(opts: {
  seedItems: readonly SeedItem[];
  prefillItems: readonly SeedItem[];
  droppedIds: ReadonlySet<string>;
  poolLocked: boolean;
}): SeedItem[] {
  const byId = new Map<string, SeedItem>();
  for (const it of opts.seedItems) byId.set(it.id, it);
  for (const it of opts.prefillItems) byId.set(it.id, it);
  const all = Array.from(byId.values());
  return opts.poolLocked ? all : all.filter((it) => !opts.droppedIds.has(it.id));
}

/**
 * Participants still to enrol. `createEvent` already writes the track's active
 * roster for a track-bound session and the engine's addParticipant does not
 * dedupe, so re-adding the seeded roster would double every one of them on the
 * sitting.
 */
export function participantsToEnrol(
  picked: readonly SeedPerson[],
  alreadyEnrolledUserIds: ReadonlySet<string>,
): SeedPerson[] {
  const seen = new Set<string>();
  return picked.filter((p) => {
    if (!p.userId || alreadyEnrolledUserIds.has(p.userId) || seen.has(p.userId)) return false;
    seen.add(p.userId);
    return true;
  });
}

// ─── S4: values ──────────────────────────────────────────────────────────────

/** The engine's session value scale (SPEC-00 — the desktop ValuesEditor grid). */
export const VALUE_MIN = 0;
export const VALUE_MAX = 10;

/**
 * Key a draft value by something stable *before* the session exists.
 *
 * The phone wizard collects S4 before `createEvent`, so typed options have no
 * id yet — the engine mints those inside `addItems`. Platform items key by id;
 * typed ones key by title and are resolved once the engine hands back the rows
 * it created.
 */
export function valueDraftKey(item: { id?: string; title: string; typed?: boolean }): string {
  return item.typed || !item.id ? `type:${item.title.trim().toLowerCase()}` : item.id;
}

export function clampValue(n: number): number {
  if (!Number.isFinite(n)) return VALUE_MIN;
  return Math.max(VALUE_MIN, Math.min(VALUE_MAX, Math.round(n)));
}

/** draftKey -> criterionId -> value */
export type ValueDraft = Record<string, Record<string, number>>;

/**
 * Resolve a pre-create value draft against the items the engine actually made.
 *
 * Returns the flat `setValue` calls to replay. Anything that cannot be matched
 * to a real item or a surviving criterion is dropped rather than written under
 * a stale key — a value on an item that is not in the session skews nothing,
 * but a value under a wrong id silently does.
 */
export function resolveValueDraft(
  draft: ValueDraft,
  engineItems: readonly { id: string; title: string; source?: string }[],
  criterionIds: readonly string[],
): { itemId: string; criterionId: string; value: number }[] {
  const byKey = new Map<string, string>();
  for (const it of engineItems) {
    byKey.set(it.id, it.id);
    byKey.set(`type:${it.title.trim().toLowerCase()}`, it.id);
  }
  const validCriteria = new Set(criterionIds);

  const out: { itemId: string; criterionId: string; value: number }[] = [];
  for (const [key, perCriterion] of Object.entries(draft)) {
    const itemId = byKey.get(key);
    if (!itemId) continue;
    for (const [criterionId, value] of Object.entries(perCriterion)) {
      if (!validCriteria.has(criterionId)) continue;
      out.push({ itemId, criterionId, value: clampValue(value) });
    }
  }
  return out;
}
